import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = {
    placeholderImageUrl: String
  }

  async fill(event) {
    event.preventDefault()

    this.fillNativeFields()
    this.fillDateRanges()
    this.fillMarketingVariants()
    this.fillMultiSelects()
    await this.fillFileFields()
    this.dispatchFormEvents()
  }

  fillNativeFields() {
    this.fields.forEach((field) => {
      if (this.shouldSkipField(field)) return

      if (field.tagName === "SELECT") {
        this.fillSelect(field)
      } else if (field.type === "checkbox") {
        field.checked = true
      } else if (field.type === "date") {
        this.setValue(field, this.dateFromNow(field.name.includes("application_deadline") ? 20 : 30))
      } else if (field.type === "number") {
        this.setValue(field, this.numberValueFor(field))
      } else if (field.tagName === "TEXTAREA") {
        this.setValue(field, this.textValueFor(field, true))
      } else {
        this.setValue(field, this.textValueFor(field))
      }
    })
  }

  fillDateRanges() {
    this.element.querySelectorAll("[data-date-range-list-target='row']").forEach((row, index) => {
      const startInput = row.querySelector("[data-date-range-start]")
      const endInput = row.querySelector("[data-date-range-end]")
      if (!startInput || !endInput) return

      this.setValue(startInput, this.dateFromNow(30 + index * 14))
      this.setValue(endInput, this.dateFromNow(32 + index * 14))
    })
  }

  fillMarketingVariants() {
    const expectedStudentCount = this.element.querySelector("[name='course[expected_student_count]']")?.value || "10"

    this.element.querySelectorAll("[data-marketing-product-variants-target='row']").forEach((row, index) => {
      const targetGroup = row.querySelector("[data-marketing-variant-target-group]")
      const price = row.querySelector("[name$='[price]']")
      const capacity = row.querySelector("[name$='[capacity]']")

      if (targetGroup) this.setValue(targetGroup, this.optionValueAt(targetGroup, index) || "A")
      if (price) this.setValue(price, "1200")
      if (capacity) this.setValue(capacity, index === 0 ? expectedStudentCount : "0")
    })
  }

  fillMultiSelects() {
    this.element.querySelectorAll("select-panel[data-select-variant='multiple']").forEach((panel) => {
      const items = Array.from(panel.items || this.selectPanelItems(panel)).filter((item) => {
        const content = item.querySelector(".ActionListContent")
        return content?.dataset.value && content.getAttribute("aria-disabled") !== "true"
      })

      items.slice(0, 2).forEach((item) => {
        if (typeof panel.checkItem === "function") {
          panel.checkItem(item)
        } else {
          item.querySelector(".ActionListContent")?.click()
        }
      })

      panel.dispatchEvent(new CustomEvent("itemActivated", { bubbles: true }))
      panel.dispatchEvent(new CustomEvent("panelClosed", { bubbles: true }))
    })
  }

  async fillFileFields() {
    if (!this.hasPlaceholderImageUrlValue) return

    const fileFields = this.fields.filter((field) => field.type === "file" && !field.disabled)
    if (fileFields.length === 0) return

    try {
      const response = await fetch(this.placeholderImageUrlValue)
      if (!response.ok) return

      const blob = await response.blob()
      fileFields.forEach((field) => {
        const dataTransfer = new DataTransfer()
        dataTransfer.items.add(new File([blob], "dev-placeholder.jpg", { type: blob.type || "image/jpeg" }))
        field.files = dataTransfer.files
        this.dispatchFieldEvents(field)
      })
    } catch (_error) {
      // File inputs cannot be filled by value; if the placeholder fetch fails, keep the rest of autofill usable.
    }
  }

  dispatchFormEvents() {
    this.element.querySelectorAll("[data-controller~='date-range-list'], [data-controller~='marketing-product-variants']").forEach((element) => {
      element.dispatchEvent(new Event("input", { bubbles: true }))
      element.dispatchEvent(new Event("change", { bubbles: true }))
    })

    this.element.dispatchEvent(new Event("input", { bubbles: true }))
    this.element.dispatchEvent(new Event("change", { bubbles: true }))
  }

  fillSelect(field) {
    const key = this.fieldKey(field)
    if (key === "held_at") {
      this.setValue(field, this.optionValue(field, "Na fakultě", "on_faculty"))
      return
    }

    this.setValue(field, this.optionValueAt(field, 0))
  }

  numberValueFor(field) {
    const key = this.fieldKey(field)
    const values = {
      credit_count: "3",
      expected_student_count: "10",
      program_scope_hours: "24",
      travel_costs_total: "1000",
      dpp_costs: "1000",
      student_accommodation_meal_price: "500",
      other_collected_student_fees: "100",
      staff_accommodation_meal_costs_total: "1000",
      other_services_purchases_total: "0",
      other_course_income: "0",
      promotion_costs: "500",
      direct_wage_costs: "1000",
      direct_material_costs: "0",
      preferred_price: "1200",
      teaching_days: "3"
    }

    return values[key] || "10"
  }

  textValueFor(field, longText = false) {
    const key = this.fieldKey(field)
    const suffix = Math.floor(Date.now() / 1000).toString().slice(-5)
    const values = {
      name_cs: `Testovací kurz ${suffix}`,
      name_en: `Test course ${suffix}`,
      organizer: "FTVS",
      center_number: "12345",
      teaching_payment_center_number: "12345",
      program_accreditation_number: "AKR-2026-001",
      isced_code: "1014",
      program_guarantor: "Jan Novak",
      venue: "Praha",
      academic_year: "2026/2027",
      start_term: "ZS",
      program_duration: "3",
      program_duration_details: "3 dny",
      implementation_schedule: "blokova vyuka",
      application_contact_person: "Jan Novak",
      application_contact_email: "jan.novak@example.test",
      application_contact_phone: "+420777123456",
      keywords: "sport, kurz, vzdelavani",
      cover_image_note: "Pouzit obecnou fotografii kurzu.",
      center_contact: "centrum@example.test",
      photo: "fotografie bude dodana",
      classification: "profesni",
      implementation_time: "jaro 2027",
      accreditation_institution_name: "MSMT",
      university_faculty_accreditation_number: "UK-FTVS-001",
      participation_conditions: "Ukoncene stredoskolske vzdelani.",
      required_entry_education: "stredoskolske",
      funding_source: "samoplatci",
      lifelong_learning_purpose: "rozvoj odbornych kompetenci",
      final_exam_information: "zaverecny test",
      acquired_qualification: "osvedceni",
      certificate_law_regulation: "dle platnych predpisu"
    }

    if (values[key]) return values[key]
    if (longText) return "Ukazkovy popis kurzu pro vyvojove testovani formulare."

    return "Testovaci hodnota"
  }

  shouldSkipField(field) {
    if (field.disabled || field.readOnly || !field.name?.startsWith("course[")) return true
    if (field.type === "hidden" || field.type === "file" || field.type === "submit" || field.type === "button") return true
    if (field.name.endsWith("[][start_date]") || field.name.endsWith("[][end_date]")) return true
    if (field.name.endsWith("[target_group]") || field.name.endsWith("[price]") || field.name.endsWith("[capacity]")) return true
    if (field.type === "checkbox" && field.name.endsWith("[]")) return true

    return false
  }

  setValue(field, value) {
    if (value === undefined || value === null || value === "") return

    field.value = value
    this.dispatchFieldEvents(field)
  }

  dispatchFieldEvents(field) {
    field.dispatchEvent(new Event("input", { bubbles: true }))
    field.dispatchEvent(new Event("change", { bubbles: true }))
  }

  optionValue(field, ...preferredValues) {
    return Array.from(field.options).find((option) => preferredValues.includes(option.value) && !option.disabled)?.value || this.optionValueAt(field, 0)
  }

  optionValueAt(field, index) {
    const options = Array.from(field.options).filter((option) => option.value !== "" && !option.disabled)
    return options[index]?.value || options[0]?.value
  }

  selectPanelItems(panel) {
    return Array.from(panel.querySelectorAll(".ActionListContent[data-value]")).map((content) => content.closest("li")).filter(Boolean)
  }

  fieldKey(field) {
    return field.name?.match(/^course\[([^\]]+)\]/)?.[1]
  }

  dateFromNow(days) {
    const date = new Date()
    date.setDate(date.getDate() + days)
    return date.toISOString().slice(0, 10)
  }

  get fields() {
    return Array.from(this.element.querySelectorAll("input, select, textarea"))
  }
}
